/**
 * My Assets — assets currently assigned to the logged-in employee
 * Authority: any authenticated user (no special authority required)
 * Backend: GET /api/hr/assets/my-assets
 */
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import { assetApi } from "../api";
import { useAsyncData } from "../hooks";
import {
  AssetError, AssetLoading, AssetStatusBadge, BackBtn, EmptyState, PageHeader, fmt, fmtDate,
} from "../components/AssetUI";

export default function AssetMyAssetsPage() {
  const { data, loading, error, reload } = useAsyncData(() => assetApi.myAssets(), []);

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Asset Management"
        title="My Assets"
        actions={<BackBtn to="/assets" label="Dashboard" />}
      />

      {loading && <AssetLoading label="Loading your assets…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        data.content.length === 0 ? (
          <EmptyState icon={Package} title="No assets assigned to you" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.content.map((a) => (
              <Link
                key={a.id}
                to={`/assets/${a.id}`}
                className="flex items-start gap-4 rounded-2xl border bg-white p-5 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-xs font-bold text-indigo-700">
                  {a.categoryName.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-slate-800">{a.name}</div>
                  <div className="text-xs text-slate-400 font-mono">{a.assetNumber}</div>
                  <div className="mt-1 text-xs text-slate-400">{a.categoryName}{a.locationName ? ` · ${a.locationName}` : ""}</div>
                  {a.purchaseCost != null && (
                    <div className="mt-1 text-xs text-slate-400">Cost: {fmt(a.purchaseCost)}</div>
                  )}
                  {a.createdAt && (
                    <div className="mt-0.5 text-xs text-slate-400">Added {fmtDate(a.createdAt)}</div>
                  )}
                </div>
                <AssetStatusBadge value={a.status} />
              </Link>
            ))}
          </div>
        )
      )}
    </div>
  );
}
